import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Breadcrumbs, 
  Link, 
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StorageIcon from '@mui/icons-material/Storage';
import CircleIcon from '@mui/icons-material/Circle';
import { useNavigate } from 'react-router-dom';

// Mockdaten für die Serverdarstellung
interface Server {
  id: string;
  name: string;
  ip: string;
  status: 'online' | 'offline' | 'warning';
  type: string;
  location: string;
  connectedDevices: number;
  lastUpdated: string;
}

const mockServers: Server[] = [
  {
    id: 'server1',
    name: 'Hauptserver Berlin',
    ip: '192.168.1.101',
    status: 'online',
    type: 'Produktionsserver',
    location: 'Berlin Datacenter',
    connectedDevices: 12,
    lastUpdated: new Date().toISOString()
  },
  {
    id: 'server2',
    name: 'Backupserver München',
    ip: '192.168.1.102',
    status: 'online',
    type: 'Backup',
    location: 'München Datacenter',
    connectedDevices: 5,
    lastUpdated: new Date().toISOString()
  },
  {
    id: 'server3',
    name: 'Testserver',
    ip: '192.168.1.103',
    status: 'warning',
    type: 'Test',
    location: 'Frankfurt Testcenter',
    connectedDevices: 3,
    lastUpdated: new Date().toISOString()
  },
  {
    id: 'server4',
    name: 'Entwicklungsserver',
    ip: '192.168.1.104',
    status: 'offline',
    type: 'Entwicklung',
    location: 'Hamburg Devoffice',
    connectedDevices: 0,
    lastUpdated: new Date(Date.now() - 86400000).toISOString()
  }
];

const ServerAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [servers, setServers] = useState<Server[]>([]);
  const [selectedServer, setSelectedServer] = useState<Server | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Simuliere API-Abruf
    const fetchServers = async () => {
      setIsLoading(true);
      try {
        // Hier würde normalerweise ein API-Aufruf stehen
        await new Promise(resolve => setTimeout(resolve, 800)); // Simuliere Ladezeit
        setServers(mockServers);
        setError(null);
      } catch (err) {
        console.error('Fehler beim Laden der Server:', err);
        setError('Die Server konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchServers();
  }, []);

  const handleServerSelect = (server: Server) => {
    setSelectedServer(server);
  };

  const handleBackClick = () => {
    setSelectedServer(null);
  };

  const handleNavigateToBackoffice = () => {
    navigate('/backoffice');
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const filteredServers = servers.filter(server => 
    server.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    server.ip.includes(searchTerm) ||
    server.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    server.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Helfer-Funktion für die Statusfarbe
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'online': return '#30D158';  // Grün
      case 'offline': return '#FF3B30'; // Rot
      case 'warning': return '#FF9500'; // Orange
      default: return '#8E8E93';        // Grau
    }
  };

  // Format das Datum für bessere Lesbarkeit
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs Navigation */}
      <Breadcrumbs aria-label="Navigationspfad" sx={{ mb: 2 }}>
        <Link color="inherit" href="/dashboard" sx={{ textDecoration: 'none' }}>
          Dashboard
        </Link>
        <Link color="inherit" href="/backoffice" sx={{ textDecoration: 'none' }}>
          Administration
        </Link>
        <Typography color="text.primary">Serververwaltung</Typography>
      </Breadcrumbs>

      {/* Seitentitel und Aktionen */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {selectedServer && (
            <IconButton onClick={handleBackClick} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h4" component="h1">
            {selectedServer ? `Server: ${selectedServer.name}` : 'Serververwaltung'}
          </Typography>
        </Box>
        
        {!selectedServer && (
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            sx={{ borderRadius: '8px' }}
          >
            Neuer Server
          </Button>
        )}
      </Box>

      {/* Fehlermeldung anzeigen, falls vorhanden */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Lade-Indikator */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Server-Liste oder Detailansicht */}
          {selectedServer ? (
            // Placeholder für Serverdetails (würde in einer echten Implementierung ausgebaut werden)
            <Box>
              <Typography variant="h6">Details werden hier angezeigt</Typography>
            </Box>
          ) : (
            <>
              {/* Suchfeld */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  placeholder="Server suchen..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Server-Liste */}
              <Grid container spacing={3}>
                {filteredServers.length === 0 ? (
                  <Grid item xs={12}>
                    <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
                      Keine Server gefunden.
                    </Typography>
                  </Grid>
                ) : (
                  filteredServers.map(server => (
                    <Grid item xs={12} sm={6} md={4} key={server.id}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.1)',
                          }
                        }}
                        onClick={() => handleServerSelect(server)}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <StorageIcon sx={{ color: 'primary.main', mr: 1 }} />
                              <Typography variant="h6" component="div">
                                {server.name}
                              </Typography>
                            </Box>
                            <Chip 
                              icon={<CircleIcon sx={{ fontSize: '0.8rem !important' }} />}
                              label={server.status.charAt(0).toUpperCase() + server.status.slice(1)}
                              size="small"
                              sx={{ 
                                backgroundColor: `${getStatusColor(server.status)}20`,
                                color: getStatusColor(server.status),
                                fontWeight: 'bold',
                                '& .MuiChip-icon': {
                                  color: getStatusColor(server.status),
                                  marginLeft: '8px',
                                }
                              }}
                            />
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            <strong>IP:</strong> {server.ip}
                          </Typography>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            <strong>Typ:</strong> {server.type}
                          </Typography>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            <strong>Standort:</strong> {server.location}
                          </Typography>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            <strong>Verbundene Geräte:</strong> {server.connectedDevices}
                          </Typography>
                          
                          <Typography variant="body2" color="text.secondary">
                            <strong>Letztes Update:</strong> {formatDate(server.lastUpdated)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                )}
              </Grid>
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default ServerAdmin; 